/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import {
  CreateUserRequest,
  DeleteUserRequest,
  TenantDetails,
  TenantDetailsUpdateRequest,
  TenantDetailsWithFlags,
  User
} from 'app/page-modules/tenant-management/models';
import { Observable } from 'rxjs';

// -------------------------------------------------------------------------
/** Implementations allow obtaining data for the tenant screens. */
export abstract class TenantDataService {
  /**
   * Create a new tenant
   * @param request the tenant creation request
   * @return the created tenant details
   */
  abstract createTenant(request: TenantDetails): Observable<TenantDetails>;

  /**
   * Returns the tenant details for a tenant
   * @param tenantName the name of the tenant to fetch the details of
   * @return the tenant details
   */
  abstract tenantDetails(tenantName: string): Observable<TenantDetailsWithFlags>;

  /**
   * Deletes a tenant profile
   * @param tenantName the name of the tenant to delete
   * @return success if the user profile was delete successfully
   */
  abstract deleteTenant(tenantName: string): Observable<void>;

  /**
   * Updates the attributes of an already existing tenant
   * @param updateRequest contains the fields to update and the success message to display on success
   * @param successMessage the message to display as a snackbar notification if request successful
   * @param organizationId the organization id to issue the update for
   * @return a success result if the details were updated successfully
   */
  abstract updateTenantDetails(
    organizationId: string,
    successMessage: string,
    updateRequest: TenantDetailsUpdateRequest
  ): Observable<TenantDetailsUpdateRequest>;

  /**
   * Adds the read-only role to the tenant, if missing.
   * @param organizationId the organization ID
   * @return nothing
   */
  abstract addReadOnlyRole(organizationId: string): Observable<void>;

  // -------------------------------------------------------------------------

  /**
   * Returns the users for a tenant
   * @param tenantName the name of the tenant to fetch the users of
   * @return the users for the tenant
   */
  abstract usersForTenant(tenantName: string): Observable<User[]>;

  /**
   * Adds a user to the list of users for a tenant
   * @param organizationId the ID of the organization
   * @param createUserRequest the user creation request
   * @return the added user
   */
  abstract addUserForTenant(
    organizationId: string,
    createUserRequest: CreateUserRequest
  ): Observable<User>;

  /**
   * Updates a user profile for a specific tenant
   * @param organizationId the ID of the organization
   * @param user the updated user profile
   * @return the updated user
   */
  abstract updateUserForTenant(organizationId: string, user: User): Observable<User>;

  /**
   * Deletes a user with a specific detail
   * @param user the user to delete
   * @param deleteUserRequest the user deletion request
   * @return the deleted user
   */
  abstract deleteUser(user: User, deleteUserRequest: DeleteUserRequest): Observable<User>;
}
